
import * as React from "react";
import * as ReactDOM from "react-dom";
import type { Person, Status } from "@/lib/types";
import { STATUS_LABELS, DESCRIPTION_MAX } from "@/lib/constants";

// Near-limit character counter for the initiative description (create form and
// edit mode share it). Announced politely so a screen-reader user learns the
// cap before maxLength silently stops accepting keystrokes; the at-limit state
// is spoken as well as colored.
export function DescriptionCounter({ length }: { length: number }) {
  const atLimit = length >= DESCRIPTION_MAX;
  return (
    <span className={`counter ${atLimit ? "at-limit" : ""}`} aria-live="polite" aria-atomic="true">
      {length}/{DESCRIPTION_MAX}
      {atLimit && <span className="sr-only"> character limit reached</span>}
    </span>
  );
}

export function Avatar({ person, size = "md" }: { person: Person | undefined | null; size?: "sm" | "md" | "lg" }) {
  if (!person) return null;
  const cls = size === "sm" ? "avatar sm" : size === "lg" ? "avatar lg" : "avatar";
  return (
    <span className={cls} style={{ background: person.color }} title={person.name}>
      {person.initials}
    </span>
  );
}

export function StatusPill({ status }: { status: Status }) {
  return (
    <span className={`status-pill s-${status}`}>
      <span className="swatch" />
      {STATUS_LABELS[status]}
    </span>
  );
}

// Anchored popover.
//
// Rendered in a portal with fixed positioning and re-measured on scroll and
// resize, so it stays attached to its anchor instead of drifting with the page.
// It sizes itself to the room actually available (flipping above the anchor
// when that side has more), clamps inside the viewport, contains its own
// scroll so a list that bottoms out never moves the page, and closes when the
// anchor scrolls out of view.
//
// Keyboard: arrow keys / Home / End move between rows marked `data-pop-item`
// (and any <input>), Escape closes and returns focus to the anchor, tabbing
// out closes. Pass `initialFocus="none"` to leave focus on the anchor.
//
// Structure hints for children: wrap a pinned header in `.popover-head` and a
// pinned footer in `.popover-foot`; the popover adds `has-more` while content
// remains below the fold so the footer can show a fade.
type Placement = { top: number; left: number; maxHeight: number; above: boolean };

const GAP = 6;      // anchor → popover
const MARGIN = 8;   // viewport edge
const MIN_HEIGHT = 120;

export function Popover({
  open,
  anchor,
  onClose,
  children,
  align = "left",
  className,
  maxHeight = 320,
  initialFocus = "first",
}: {
  open: boolean;
  anchor: HTMLElement | null;
  onClose: () => void;
  children: React.ReactNode;
  align?: "left" | "right";
  className?: string;
  /** Upper bound; the popover shrinks below this to fit the viewport. */
  maxHeight?: number;
  initialFocus?: "first" | "none";
}) {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = React.useState<Placement | null>(null);
  const [hasMore, setHasMore] = React.useState(false);

  const updateHasMore = React.useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setHasMore(el.scrollTop + el.clientHeight < el.scrollHeight - 2);
  }, []);

  const place = React.useCallback(() => {
    const el = ref.current;
    if (!el || !anchor) return;
    const a = anchor.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    // Anchor left the viewport — a menu floating over unrelated content helps nobody.
    if (a.bottom < 0 || a.top > vh || a.right < 0 || a.left > vw) {
      onClose();
      return;
    }
    // scrollHeight is the padding box; add the border so a list that exactly
    // fits doesn't end up 2px scrollable.
    const chrome = el.offsetHeight - el.clientHeight;
    const want = Math.min(maxHeight, el.scrollHeight + chrome);
    const roomBelow = vh - a.bottom - GAP - MARGIN;
    const roomAbove = a.top - GAP - MARGIN;
    const above = want > roomBelow && roomAbove > roomBelow;
    const room = above ? roomAbove : roomBelow;
    const h = Math.max(MIN_HEIGHT, Math.min(want, room));
    const top = above ? Math.max(MARGIN, a.top - GAP - h) : a.bottom + GAP;
    const w = el.offsetWidth;
    const rawLeft = align === "right" ? a.right - w : a.left;
    const left = Math.max(MARGIN, Math.min(rawLeft, vw - w - MARGIN));
    setPos(prev =>
      prev && prev.top === top && prev.left === left && prev.maxHeight === h && prev.above === above
        ? prev
        : { top, left, maxHeight: h, above },
    );
  }, [anchor, align, maxHeight, onClose]);

  // Measure before paint on open, then follow the anchor.
  React.useLayoutEffect(() => {
    if (!open) { setPos(null); return; }
    place();
    // Scroll and resize already fire at most once per frame, and place() is
    // one rect read plus a bail-out setState, so no extra throttling.
    const onMove = () => place();
    window.addEventListener("scroll", onMove, { capture: true, passive: true });
    window.addEventListener("resize", onMove);
    const ro = ref.current ? new ResizeObserver(onMove) : null;
    if (ro && ref.current) ro.observe(ref.current);
    return () => {
      window.removeEventListener("scroll", onMove, { capture: true });
      window.removeEventListener("resize", onMove);
      ro?.disconnect();
    };
  }, [open, place]);

  React.useEffect(() => { if (pos) updateHasMore(); }, [pos, children, updateHasMore]);

  // Initial focus — the first input if there is one (type-to-filter), else the
  // first row. Skipped on touch devices, where focusing an input would throw
  // the on-screen keyboard over the list the moment it opens.
  const focusedOnOpen = React.useRef(false);
  React.useEffect(() => {
    if (!open || !pos || focusedOnOpen.current || initialFocus === "none") return;
    focusedOnOpen.current = true;
    if (window.matchMedia?.("(pointer: coarse)").matches) return;
    const el = ref.current;
    const first = el?.querySelector<HTMLElement>("input, [data-pop-item]");
    first?.focus({ preventScroll: true });
  }, [open, pos, initialFocus]);
  React.useEffect(() => { if (!open) focusedOnOpen.current = false; }, [open]);

  // Outside click + Escape.
  React.useEffect(() => {
    if (!open) return;
    const handle = (e: MouseEvent) => {
      const t = e.target as Node;
      if (ref.current && !ref.current.contains(t) && anchor && !anchor.contains(t)) {
        onClose();
      }
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      onClose();
      anchor?.focus({ preventScroll: true });
    };
    document.addEventListener("mousedown", handle);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", handle);
      document.removeEventListener("keydown", esc);
    };
  }, [open, anchor, onClose]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const el = ref.current;
    if (!el) return;
    const inInput = (e.target as HTMLElement).tagName === "INPUT";
    if (inInput && (e.key === "Home" || e.key === "End")) return;
    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(e.key)) return;
    const items = Array.from(el.querySelectorAll<HTMLElement>("input, [data-pop-item]"))
      .filter(n => !n.hasAttribute("disabled"));
    if (!items.length) return;
    e.preventDefault();
    const idx = items.indexOf(document.activeElement as HTMLElement);
    let next = 0;
    if (e.key === "ArrowDown") next = idx < 0 ? 0 : Math.min(items.length - 1, idx + 1);
    else if (e.key === "ArrowUp") next = idx < 0 ? items.length - 1 : Math.max(0, idx - 1);
    else if (e.key === "Home") next = 0;
    else next = items.length - 1;
    items[next].focus();
    items[next].scrollIntoView({ block: "nearest" });
  };

  // Tabbing out closes. relatedTarget is null for clicks on non-focusable
  // areas in some browsers, so only act when focus demonstrably went elsewhere.
  const onBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    const to = e.relatedTarget as Node | null;
    if (!to) return;
    if (ref.current?.contains(to) || anchor?.contains(to)) return;
    onClose();
  };

  if (!open || !anchor) return null;
  const style: React.CSSProperties = pos
    ? { top: pos.top, left: pos.left, maxHeight: pos.maxHeight }
    : { top: 0, left: 0, maxHeight, visibility: "hidden" };
  return ReactDOM.createPortal(
    <div
      className={`popover popover--anchored fade-up ${pos?.above ? "is-above" : ""} ${hasMore ? "has-more" : ""} ${className ?? ""}`}
      ref={ref}
      style={style}
      onKeyDown={onKeyDown}
      onBlur={onBlur}
      onScroll={updateHasMore}
    >
      {children}
    </div>,
    document.body,
  );
}
